import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, List, ListItem, ListItemIcon,
  ListItemText, IconButton, Dialog, DialogContent,
  CircularProgress, Paper, Grid, Card, CardMedia,
  CardContent, CardActions, Tooltip, Skeleton
} from '@mui/material';
import {
  CloudUpload, Description, Image, PictureAsPdf,
  Delete, Download, Visibility, Close, AttachFile,
  CameraAlt
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../services/api';

const AttachmentManager = ({ ticketId, canEdit = true }) => {
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewType, setPreviewType] = useState('');
  const [previewName, setPreviewName] = useState('');
  const [thumbnails, setThumbnails] = useState({});
  const [dragActive, setDragActive] = useState(false);
  const dragCounter = React.useRef(0);

  useEffect(() => {
    loadAttachments();
    
    // Cleanup beim Unmount
    return () => {
      // Räume alle Blob-URLs auf
      Object.values(thumbnails).forEach(url => {
        if (url && url.startsWith('blob:')) {
          window.URL.revokeObjectURL(url);
        }
      });
    };
  }, [ticketId]);

  const loadAttachments = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/attachments/ticket/${ticketId}`);
      setAttachments(response.data);
      
      // Lade Thumbnails für Bilder
      response.data.forEach(attachment => {
        if (attachment.file_type?.startsWith('image/')) {
          loadThumbnail(attachment);
        }
      });
    } catch (error) {
      console.error('Fehler beim Laden der Anhänge:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadThumbnail = async (attachment) => {
    try {
      const response = await api.get(`/attachments/${attachment.id}/download`, {
        responseType: 'blob'
      });
      const blobUrl = window.URL.createObjectURL(response.data);
      setThumbnails(prev => ({
        ...prev,
        [attachment.id]: blobUrl
      }));
    } catch (error) {
      console.error('Fehler beim Laden des Thumbnails:', error);
    }
  };

  const handleFileUpload = async (event) => {
    const files = event.target?.files || event.dataTransfer?.files;
    if (!files || files.length === 0) return;

    await uploadFiles(Array.from(files));
    
    if (event.target?.value) {
      event.target.value = null; // Reset input
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setDragActive(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    dragCounter.current = 0;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await uploadFiles(Array.from(e.dataTransfer.files));
      e.dataTransfer.clearData();
    }
  };

  const uploadFiles = async (files) => {
    // Prüfe Anzahl der Dateien
    if (files.length > 10) {
      toast.error('Maximal 10 Dateien auf einmal erlaubt.');
      return;
    }

    setUploading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const file of files) {
      // Prüfe Dateigröße (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} ist zu groß. Maximal 10MB erlaubt.`);
        errorCount++;
        continue;
      }

      const formData = new FormData();
      formData.append('file', file);

      try {
        await api.post(`/attachments/ticket/${ticketId}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        successCount++;
      } catch (error) {
        errorCount++;
        toast.error(`Fehler beim Hochladen von ${file.name}: ${error.response?.data?.error || 'Unbekannter Fehler'}`);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} Datei${successCount > 1 ? 'en' : ''} erfolgreich hochgeladen`);
      loadAttachments();
    }
    
    setUploading(false);
  };

  const handleDelete = async (attachmentId) => {
    if (!window.confirm('Möchten Sie diese Datei wirklich löschen?')) return;

    try {
      await api.delete(`/attachments/${attachmentId}`);
      toast.success('Datei erfolgreich gelöscht');
      loadAttachments();
    } catch (error) {
      toast.error('Fehler beim Löschen der Datei');
    }
  };

  const handlePreview = async (attachment) => {
    try {
      // Für Bilder und PDFs: Hole die Datei mit Auth-Token
      const response = await api.get(`/attachments/${attachment.id}/download`, {
        responseType: 'blob'
      });
      
      // Erstelle Blob-URL für Vorschau
      const blobUrl = window.URL.createObjectURL(response.data);
      setPreviewUrl(blobUrl);
      setPreviewType(attachment.file_type);
      setPreviewName(attachment.file_name);
      setPreviewOpen(true);
    } catch (error) {
      toast.error('Fehler beim Laden der Vorschau');
    }
  };

  const handleDownload = async (attachment) => {
    try {
      const response = await api.get(`/attachments/${attachment.id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Fehler beim Download');
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType?.startsWith('image/')) return <Image color="primary" />;
    if (fileType === 'application/pdf') return <PictureAsPdf color="error" />;
    if (fileType?.includes('word')) return <Description color="info" />;
    return <AttachFile />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const isPreviewable = (fileType) => {
    return fileType?.startsWith('image/') || fileType === 'application/pdf';
  };

  return (
    <Box
      onDragEnter={canEdit ? handleDragEnter : undefined}
      onDragLeave={canEdit ? handleDragLeave : undefined}
      onDragOver={canEdit ? handleDragOver : undefined}
      onDrop={canEdit ? handleDrop : undefined}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Typography variant="h6">Anhänge ({attachments.length})</Typography>
        {canEdit && (
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              startIcon={<CameraAlt />}
              component="label"
              disabled={uploading}
              color="secondary"
              size="small"
            >
              Foto
              <input
                type="file"
                hidden
                accept="image/*"
                capture="environment"
                onChange={handleFileUpload}
              />
            </Button>
            <Button
              variant="contained"
              startIcon={uploading ? <CircularProgress size={20} /> : <CloudUpload />}
              component="label"
              disabled={uploading}
              size="small"
            >
              Datei hochladen
              <input
                type="file"
                hidden
                multiple
                onChange={handleFileUpload}
                accept="image/*,.pdf,.doc,.docx,.eml,.txt"
              />
            </Button>
          </Box>
        )}
      </Box>

      {canEdit && dragActive && (
        <Paper
          sx={{
            p: 4,
            mb: 2,
            border: '2px dashed',
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
            textAlign: 'center',
            cursor: 'pointer'
          }}
        >
          <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" color="primary">
            Dateien hier ablegen
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Lassen Sie die Dateien los, um sie hochzuladen
          </Typography>
        </Paper>
      )}

      {loading ? (
        <CircularProgress />
      ) : attachments.length === 0 ? (
        <Paper 
          sx={{ 
            p: 3, 
            textAlign: 'center',
            border: canEdit && !dragActive ? '2px dashed #ccc' : 'none',
            backgroundColor: canEdit && !dragActive ? 'grey.50' : 'transparent',
            cursor: canEdit ? 'pointer' : 'default'
          }}
        >
          {canEdit && (
            <>
              <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                Drag & Drop für Datei-Upload
              </Typography>
            </>
          )}
          <Typography color="textSecondary">
            {canEdit ? 'Dateien hierher ziehen oder auf "Datei hochladen" klicken' : 'Keine Anhänge vorhanden'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {attachments.map((attachment) => (
            <Grid item xs={12} sm={6} md={4} key={attachment.id}>
              <Card>
                {attachment.file_type?.startsWith('image/') ? (
                  thumbnails[attachment.id] ? (
                    <CardMedia
                      component="img"
                      height="140"
                      image={thumbnails[attachment.id]}
                      alt={attachment.file_name}
                      sx={{ cursor: 'pointer', objectFit: 'cover' }}
                      onClick={() => handlePreview(attachment)}
                    />
                  ) : (
                    <Skeleton variant="rectangular" height={140} />
                  )
                ) : (
                  <Box
                    sx={{
                      height: 140,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'grey.100'
                    }}
                  >
                    {getFileIcon(attachment.file_type)}
                  </Box>
                )}
                <CardContent>
                  <Typography variant="body2" noWrap title={attachment.file_name}>
                    {attachment.file_name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {formatFileSize(attachment.file_size)}
                  </Typography>
                  <Typography variant="caption" display="block" color="textSecondary">
                    von {attachment.uploaded_by_name}
                  </Typography>
                  <Typography variant="caption" display="block" color="textSecondary">
                    {new Date(attachment.uploaded_at).toLocaleDateString('de-DE')}
                  </Typography>
                </CardContent>
                <CardActions>
                  {isPreviewable(attachment.file_type) && (
                    <Tooltip title="Vorschau">
                      <IconButton size="small" onClick={() => handlePreview(attachment)}>
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Download">
                    <IconButton size="small" onClick={() => handleDownload(attachment)}>
                      <Download />
                    </IconButton>
                  </Tooltip>
                  {canEdit && (
                    <Tooltip title="Löschen">
                      <IconButton size="small" onClick={() => handleDelete(attachment.id)}>
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Vorschau-Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          // Räume Blob-URL auf
          if (previewUrl && previewUrl.startsWith('blob:')) {
            window.URL.revokeObjectURL(previewUrl);
          }
        }}
        maxWidth="lg"
        fullWidth
      >
        <Box sx={{ position: 'relative' }}>
          <IconButton
            sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}
            onClick={() => {
              setPreviewOpen(false);
              // Räume Blob-URL auf
              if (previewUrl && previewUrl.startsWith('blob:')) {
                window.URL.revokeObjectURL(previewUrl);
              }
            }}
          >
            <Close />
          </IconButton>
          <DialogContent>
            <Typography variant="h6" gutterBottom>
              {previewName}
            </Typography>
            {previewType?.startsWith('image/') ? (
              <img
                src={previewUrl}
                alt={previewName}
                style={{ width: '100%', height: 'auto' }}
              />
            ) : previewType === 'application/pdf' ? (
              <iframe
                src={previewUrl}
                title={previewName}
                width="100%"
                height="600px"
                style={{ border: 'none' }}
              />
            ) : (
              <Typography>Vorschau nicht verfügbar</Typography>
            )}
          </DialogContent>
        </Box>
      </Dialog>
    </Box>
  );
};

export default AttachmentManager;